<template>
    <!-- Versión compacta para barra de estado -->
    <div v-if="compact" class="progress-compact">
        <div class="compact-info">
            <i class="fas fa-tasks"></i>
            <span class="compact-text">Progreso:</span>
            <span class="compact-stats">{{ completedFields }}/{{ totalFields }}</span>
        </div>
        <div class="compact-bar-container">
            <div 
                class="compact-bar" 
                :style="{ width: `${percentage}%` }"
                :class="progressClass"
            >
                <div class="compact-shine"></div>
            </div>
        </div>
        <span class="compact-percentage" :class="progressClass">{{ percentage }}%</span>
    </div>

    <!-- Versión completa original -->
    <div v-else class="progress-indicator">
        <div class="progress-header">
            <div class="progress-title">
                <i class="fas fa-tasks"></i>
                <span>Progreso del Formulario</span>
            </div>
            <div class="progress-stats">
                <span class="completed-count">{{ completedFields }}/{{ totalFields }}</span>
                <span class="percentage">{{ percentage }}%</span>
            </div>
        </div>

        <!-- Barra de progreso -->
        <div class="progress-bar-container">
            <div 
                class="progress-bar" 
                :style="{ width: `${percentage}%` }"
                :class="progressClass"
            >
                <div class="progress-shine"></div>
            </div>
        </div>

        <!-- Lista de campos -->
        <div class="fields-list">
            <div 
                v-for="field in fields" 
                :key="field.name" 
                class="field-item"
                :class="{ 'completed': field.isCompleted, 'required': field.isRequired }"
            >
                <div class="field-icon">
                    <i v-if="field.isCompleted" class="fas fa-check-circle"></i>
                    <i v-else class="far fa-circle"></i>
                </div>
                <div class="field-info">
                    <span class="field-label">{{ field.label }}</span>
                    <span v-if="field.isRequired" class="required-tag">Requerido</span>
                </div>
                <div v-if="field.isCompleted && field.charCount" class="field-stats">
                    <span class="char-count">
                        <i class="fas fa-font"></i>
                        {{ field.charCount }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Mensaje motivacional -->
        <div class="progress-message" :class="progressClass">
            <i :class="messageIcon"></i>
            <span>{{ motivationalMessage }}</span>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
    formData: {
        type: Object,
        required: true
    },
    compact: {
        type: Boolean,
        default: false
    }
});

// Definición de campos con sus reglas
const fieldDefinitions = [
    { name: 'reason_for_visit', label: 'Motivo de la consulta', isRequired: true, type: 'text' },
    { name: 'soap', label: 'Evolución SOAP', isRequired: true, type: 'html' },
    { name: 'physical_examination', label: 'Exploración Física', isRequired: true, type: 'html' },
    { name: 'laboratory_and_studies', label: 'Laboratorio y Estudios', isRequired: true, type: 'html' },
    { name: 'diagnoses_clinical_problems', label: 'Diagnósticos', isRequired: true, type: 'html' },
    { name: 'created', label: 'Fecha de creación', isRequired: true, type: 'date' },
    { name: 'physical_examination_json', label: 'Exploración Detallada', isRequired: false, type: 'json' }
];

// Función para obtener longitud de texto (strip HTML)
const getTextLength = (html) => {
    if (!html) return 0;
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent.trim().length;
};

// Comprobar si un campo está completo
const isFieldCompleted = (field) => {
    const value = props.formData[field.name];
    
    if (!value) return false;
    
    switch (field.type) {
        case 'text':
        case 'date':
            return value.toString().trim().length > 0;
        case 'html':
            return getTextLength(value) > 0;
        case 'json':
            try {
                const obj = typeof value === 'string' ? JSON.parse(value) : value;
                return Object.keys(obj).length > 0;
            } catch {
                return false;
            }
        default:
            return false;
    }
};

// Campos con información de completado
const fields = computed(() => {
    return fieldDefinitions.map(field => ({
        ...field,
        isCompleted: isFieldCompleted(field),
        charCount: field.type === 'html' ? getTextLength(props.formData[field.name]) : null
    }));
});

// Campos completados
const completedFields = computed(() => {
    return fields.value.filter(f => f.isCompleted).length;
});

// Total de campos
const totalFields = computed(() => fieldDefinitions.length);

// Porcentaje
const percentage = computed(() => {
    if (totalFields.value === 0) return 0;
    return Math.round((completedFields.value / totalFields.value) * 100);
});

// Clase de progreso
const progressClass = computed(() => {
    const p = percentage.value;
    if (p === 100) return 'complete';
    if (p >= 75) return 'high';
    if (p >= 50) return 'medium';
    if (p >= 25) return 'low';
    return 'start';
});

// Icono del mensaje
const messageIcon = computed(() => {
    const p = percentage.value;
    if (p === 100) return 'fas fa-trophy';
    if (p >= 75) return 'fas fa-thumbs-up';
    if (p >= 50) return 'fas fa-chart-line';
    if (p >= 25) return 'fas fa-rocket';
    return 'fas fa-lightbulb';
});

// Mensaje motivacional
const motivationalMessage = computed(() => {
    const p = percentage.value;
    if (p === 100) return '¡Excelente! Formulario completo';
    if (p >= 75) return '¡Casi terminas! Solo faltan algunos campos';
    if (p >= 50) return '¡Buen progreso! Continúa así';
    if (p >= 25) return '¡Buen comienzo! Sigue adelante';
    return 'Comienza a completar los campos requeridos';
});
</script>

<style scoped>
.progress-indicator {
    background: white;
    border-radius: 16px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;
}

.progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.progress-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: #2d3748;
}

.progress-title i {
    color: #667eea;
}

.progress-stats {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.completed-count {
    font-weight: 600;
    color: #667eea;
    font-size: 1.1rem;
}

.percentage {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 0.25rem 0.65rem;
    border-radius: 12px;
    font-size: 0.85rem;
    font-weight: 600;
}

.progress-bar-container {
    height: 12px;
    background: #edf2f7;
    border-radius: 20px;
    overflow: hidden;
    margin-bottom: 1.25rem;
    position: relative;
}

.progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
    border-radius: 20px;
    transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.progress-bar.complete {
    background: linear-gradient(90deg, #48bb78 0%, #38a169 100%);
}

.progress-bar.high {
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
}

.progress-bar.medium {
    background: linear-gradient(90deg, #4299e1 0%, #3182ce 100%);
}

.progress-bar.low {
    background: linear-gradient(90deg, #ed8936 0%, #dd6b20 100%);
}

.progress-bar.start {
    background: linear-gradient(90deg, #fc8181 0%, #f56565 100%);
}

.progress-shine {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
    );
    animation: shine 2s infinite;
}

@keyframes shine {
    to {
        left: 100%;
    }
}

.fields-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.field-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    border-radius: 10px;
    background: #f7fafc;
    transition: all 0.3s ease;
}

.field-item.completed {
    background: linear-gradient(135deg, #f0fff4 0%, #e6fffa 100%);
}

.field-icon {
    font-size: 1.2rem;
}

.field-item .fa-check-circle {
    color: #48bb78;
}

.field-item .fa-circle {
    color: #cbd5e0;
}

.field-info {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.field-label {
    color: #4a5568;
    font-size: 0.9rem;
}

.field-item.completed .field-label {
    color: #2f855a;
    font-weight: 500;
}

.required-tag {
    background: linear-gradient(135deg, #fc8181 0%, #f56565 100%);
    color: white;
    font-size: 0.7rem;
    padding: 0.2rem 0.5rem;
    border-radius: 6px;
    font-weight: 600;
}

.field-stats {
    display: flex;
    gap: 0.75rem;
    font-size: 0.8rem;
    color: #718096;
}

.char-count {
    display: flex;
    align-items: center;
    gap: 0.3rem;
}

.progress-message {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.85rem 1.1rem;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 500;
}

.progress-message.complete {
    background: linear-gradient(135deg, #f0fff4 0%, #e6fffa 100%);
    color: #2f855a;
}

.progress-message.high {
    background: linear-gradient(135deg, #ebf4ff 0%, #e9d8fd 100%);
    color: #553c9a;
}

.progress-message.medium {
    background: linear-gradient(135deg, #e6f2ff 0%, #bee3f8 100%);
    color: #2c5282;
}

.progress-message.low {
    background: linear-gradient(135deg, #fffaf0 0%, #feebc8 100%);
    color: #7c2d12;
}

.progress-message.start {
    background: linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%);
    color: #742a2a;
}

.progress-message i {
    font-size: 1.2rem;
}

/* Estilos para versión compacta */
.progress-compact {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0;
}

.compact-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.85rem;
    color: #4a5568;
    white-space: nowrap;
}

.compact-info i {
    color: #805ad5;
    font-size: 1rem;
}

.compact-text {
    font-weight: 500;
}

.compact-stats {
    font-weight: 600;
    color: #2d3748;
}

.compact-bar-container {
    flex: 1;
    height: 6px;
    background: #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
    position: relative;
    min-width: 100px;
    max-width: 200px;
}

.compact-bar {
    height: 100%;
    border-radius: 10px;
    transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.compact-bar.complete {
    background: linear-gradient(90deg, #48bb78 0%, #38a169 100%);
}

.compact-bar.high {
    background: linear-gradient(90deg, #805ad5 0%, #6b46c1 100%);
}

.compact-bar.medium {
    background: linear-gradient(90deg, #4299e1 0%, #3182ce 100%);
}

.compact-bar.low {
    background: linear-gradient(90deg, #ed8936 0%, #dd6b20 100%);
}

.compact-bar.start {
    background: linear-gradient(90deg, #f56565 0%, #e53e3e 100%);
}

.compact-shine {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
    );
    animation: shine 2s infinite;
}

.compact-percentage {
    font-size: 0.85rem;
    font-weight: 700;
    min-width: 42px;
    text-align: right;
}

.compact-percentage.complete {
    color: #38a169;
}

.compact-percentage.high {
    color: #6b46c1;
}

.compact-percentage.medium {
    color: #3182ce;
}

.compact-percentage.low {
    color: #dd6b20;
}

.compact-percentage.start {
    color: #e53e3e;
}

@keyframes shine {
    0% {
        left: -100%;
    }
    100% {
        left: 100%;
    }
}

/* Responsive */
@media (max-width: 768px) {
    .progress-indicator {
        padding: 1rem;
    }
    
    .progress-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.5rem;
    }
    
    .field-item {
        flex-wrap: wrap;
    }
    
    .field-stats {
        width: 100%;
        justify-content: flex-end;
    }
}
</style>
